import dayjs from 'dayjs';
import { ContractDate } from '../Generated/Mark-It-View-Api';

// Fiscal-year math shared by the commodity-management dashboard.
//
// Convention (matches ContractDateHelper.getStartContractDate / getFiscalYear
// and the notes in Dashboard/ChatFiscalYear.ts):
//   start month = fiscalMonthOffset + 1
//   end month   = fiscalMonthOffset (of the following year)
// So offset 0  -> fiscal year N runs Jan N  -> Dec N
//    offset 6  -> fiscal year N runs Jul N  -> Jun (N+1)
//    offset 9  -> fiscal year N runs Oct N  -> Sep (N+1)

export interface FiscalMonth {
  year: number;
  month: number; // 1-12
}

function normalizeOffset(fiscalMonthOffset: number | undefined | null): number {
  return Math.max(0, Math.min(11, Math.floor(fiscalMonthOffset ?? 0)));
}

/** The fiscal year that a given calendar month falls into. */
export function getFiscalYearForMonth(year: number, month: number, fiscalMonthOffset: number | undefined | null): number {
  const offset = normalizeOffset(fiscalMonthOffset);
  return month > offset ? year : year - 1;
}

/** The fiscal year that today falls into. */
export function getCurrentFiscalYear(fiscalMonthOffset: number | undefined | null): number {
  const now = dayjs();
  return getFiscalYearForMonth(now.year(), now.month() + 1, fiscalMonthOffset);
}

/** The 12 budget months of a fiscal year, in chronological order. */
export function getFiscalYearMonths(fiscalYear: number, fiscalMonthOffset: number | undefined | null): FiscalMonth[] {
  const offset = normalizeOffset(fiscalMonthOffset);
  const months: FiscalMonth[] = [];
  for (let i = 0; i < 12; i++) {
    const monthIndex = offset + i; // 0-based from January of `fiscalYear`
    months.push({ year: fiscalYear + Math.floor(monthIndex / 12), month: (monthIndex % 12) + 1 });
  }
  return months;
}

/**
 * The dropdown label for a fiscal year. Calendar-aligned customers (offset 0)
 * get a bare year; everyone else gets the straddled "2026/2027" form.
 */
export function formatFiscalYearLabel(fiscalYear: number, fiscalMonthOffset: number | undefined | null): string {
  return normalizeOffset(fiscalMonthOffset) === 0 ? `${fiscalYear}` : `${fiscalYear}/${fiscalYear + 1}`;
}

/** Parse back the leading year from a label produced by formatFiscalYearLabel. */
export function parseFiscalYearLabel(label: string): number | undefined {
  const year = parseInt(label.split('/')[0], 10);
  return Number.isNaN(year) ? undefined : year;
}

/**
 * Monotonic sort key for a year/month pair. Use this instead of
 * dayjs(label, 'MMM-YY') - day.js only honours a custom parse format when the
 * customParseFormat plugin is registered, which this app does not do globally,
 * so 'Jul-26' silently parses as 26 July 2001 and the year is lost.
 */
export function monthSortKey(date: ContractDate | FiscalMonth | undefined | null): number {
  if (!date || date.year == null || date.month == null) return Number.NEGATIVE_INFINITY;
  return date.year * 12 + date.month;
}
